import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Users, Inbox, Phone, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { SEO } from "@/components/SEO";

const STATUS_COLORS = {
  New: "bg-blush/20 text-blush",
  Contacted: "bg-vblue/20 text-vblue",
  Quoted: "bg-gold/20 text-gold",
  Confirmed: "bg-sage/20 text-sage",
  Completed: "bg-sage/30 text-sage",
  Cancelled: "bg-ivory/10 text-ivory/50",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/enquiries").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const cards = [
    { label: "Total Artists", value: stats?.total_artists, icon: Users, testid: "stat-total-artists" },
    { label: "Total Enquiries", value: stats?.total_enquiries, icon: Inbox, testid: "stat-total-enquiries" },
    { label: "New", value: stats?.new, testid: "stat-new" },
    { label: "Contacted", value: stats?.contacted, testid: "stat-contacted" },
    { label: "Quoted", value: stats?.quoted, testid: "stat-quoted" },
    { label: "Confirmed", value: stats?.confirmed, testid: "stat-confirmed" },
    { label: "Completed", value: stats?.completed, testid: "stat-completed" },
    { label: "Cancelled", value: stats?.cancelled, testid: "stat-cancelled" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <SEO title="Admin Dashboard" description="The Velvet Brush admin." path="/admin" />
      <h1 className="font-serif font-bold text-2xl sm:text-3xl">Dashboard</h1>
      <p className="mt-1 text-sm text-ivory/50">Overview of artists and enquiry pipeline.</p>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={c.testid} className="bg-ivory/5 border border-ivory/10 rounded-2xl p-4 sm:p-5">
            <p className="text-[11px] uppercase tracking-widest text-ivory/40">{c.label}</p>
            <p className="mt-2 font-serif text-3xl sm:text-4xl text-ivory">{c.value ?? "—"}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 flex items-center justify-between">
        <h2 className="font-serif text-xl">Recent Enquiries</h2>
        <Link to="/admin/enquiries" data-testid="view-all-enquiries" className="flex items-center gap-1 text-sm text-velvet hover:text-velvethover">
          View all <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="mt-4 flex flex-col gap-3">
        {recent.length === 0 && <p className="text-sm text-ivory/40" data-testid="no-recent-enquiries">No enquiries yet.</p>}
        {recent.map((e) => (
          <div key={e.id} data-testid={`recent-enquiry-${e.enquiry_number}`}
            onClick={() => navigate(`/admin/enquiries?open=${e.id}`)}
            className="cursor-pointer bg-ivory/5 border border-ivory/10 rounded-2xl p-4 flex items-center justify-between gap-4 hover:border-velvet/40 transition-colors">
            <div className="min-w-0">
              <p className="font-semibold truncate">{e.name} <span className="text-ivory/40 font-normal text-xs ml-2">{e.enquiry_number}</span></p>
              <p className="text-xs text-ivory/50 mt-1 truncate">{e.service} • {e.event_date} • {e.city}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <a href={`tel:${e.mobile}`} data-testid={`recent-call-${e.enquiry_number}`} onClick={(ev) => ev.stopPropagation()}
                className="text-velvet hover:text-velvethover"><Phone className="w-4 h-4" /></a>
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[e.status] || STATUS_COLORS.New}`}>{e.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
